/**
 * Cache storage type determines where cached data is persisted
 */
export enum CacheStorageType {
    NONE = 'none',
    MEMORY = 'memory',
    SESSION = 'sessionStorage',
    LOCAL = 'localStorage',
    CACHE_STORAGE = 'cacheStorage',
}

/**
 * Configuration for a cached HTTP request
 */
export interface HttpCacheConfig {
    /**
     * Type of cache storage to use
     */
    storageType: CacheStorageType;

    /**
     * Cache timeout in minutes
     * After this period, cached data is considered stale
     */
    timeoutMinutes: number;

    /**
     * Force refresh flag - if true, bypass cache and make fresh request
     */
    forceRefresh?: boolean;

    /**
     * Optional cache key prefix for namespacing
     * Helps avoid key collisions across different features
     */
    keyPrefix?: string;

    /**
     * Optional auth token for user-scoped caching
     * When provided, cache entries are namespaced by user
     * Old entries from different auth tokens are automatically cleaned up
     */
    authToken?: string;
}

/**
 * Cached response wrapper that includes timestamp metadata
 */
export interface CachedResponse<T> {
    /**
     * The actual response data
     */
    data: T;

    /**
     * ISO timestamp when the request was made and cached
     */
    cachedAt: string;

    /**
     * ISO timestamp when the cache expires (cachedAt + timeout)
     */
    expiresAt: string;

    /**
     * The cache key used to store this response
     */
    cacheKey: string;
}

/**
 * Internal structure for storing cache entries
 */
export interface CacheEntry<T> {
    data: T;
    cachedAt: string;
    expiresAt: string;
}

/**
 * Request parameters used for cache key generation
 */
export interface RequestParams {
    /**
     * HTTP method (GET, POST, etc.)
     */
    method: string;

    /**
     * Full URL including base path
     */
    url: string;

    /**
     * Query parameters as key-value pairs
     */
    queryParams?: Record<string, any>;

    /**
     * Request body for POST/PUT/PATCH requests
     */
    body?: any;

    /**
     * Additional headers that affect response (e.g., Accept-Language)
     */
    relevantHeaders?: Record<string, string>;
}

/**
 * Default cache configuration
 */
export const DEFAULT_CACHE_CONFIG: Partial<HttpCacheConfig> = {
    storageType: CacheStorageType.NONE,
    timeoutMinutes: 60,
    forceRefresh: false,
    keyPrefix: 'qwyk_cache',
    authToken: undefined,
};

/**
 * Cache key prefix used for all auth-scoped cache entries
 */
export const CACHE_KEY_PREFIX = 'cache';
